import React, { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth, effectiveTier } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import CreditCostStrip from "@/components/credits/CreditCostStrip";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { Loader2, Sparkles, Wand2, Film, Trash2, Download, RotateCcw, Share2 } from "lucide-react";
import SmartExportModal from "@/components/exports/SmartExportModal";

const PLATFORMS = [
  { id: "instagram_reels", label: "IG Reels", aspect: "9:16" },
  { id: "tiktok",          label: "TikTok",    aspect: "9:16" },
  { id: "youtube_shorts",  label: "YT Shorts", aspect: "9:16" },
  { id: "ig_feed",         label: "IG Feed",   aspect: "1:1"  },
  { id: "youtube",         label: "YouTube",   aspect: "16:9" },
];

const TONES = [
  { id: "cinematic", label: "Cinematic" },
  { id: "luxury",    label: "Luxury" },
  { id: "bold",      label: "Bold / High-energy" },
  { id: "premium",   label: "Premium" },
  { id: "warm",      label: "Warm / Inviting" },
  { id: "documentary", label: "Documentary" },
];

export default function CommercialGeneratePage() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const tier = effectiveTier(user);

  const [concept, setConcept] = useState("");
  const [totalLength, setTotalLength] = useState(30);
  const [platform, setPlatform] = useState("instagram_reels");
  const [tone, setTone] = useState("cinematic");
  const [audience, setAudience] = useState("");
  const [aspect, setAspect] = useState("9:16");
  const [quality, setQuality] = useState("pro");

  const [planning, setPlanning] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [result, setResult] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  const totalSec = useMemo(
    () => (plan?.shots || []).reduce((a, s) => a + (parseInt(s.duration) || 0), 0),
    [plan]
  );

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;

  const onPlan = async () => {
    setError("");
    setPlan(null);
    setProjectId(null);
    setResult(null);
    if (concept.trim().length < 20) {
      setError("Tell us about the commercial — at least 20 characters.");
      return;
    }
    setPlanning(true);
    try {
      const { data } = await api.post("/commercial/plan", {
        concept,
        total_length: totalLength,
        platform,
        tone,
        audience,
      });
      setPlan(data.plan);
      setProjectId(data.project_id);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPlanning(false);
    }
  };

  const onEditShot = (idx, key, value) => {
    setPlan((p) => {
      const next = { ...p, shots: [...p.shots] };
      if (key === "duration") {
        // Sora 2 API only accepts 4, 8, or 12 seconds — snap to nearest valid.
        const v = parseInt(value) || 8;
        const valid = [4, 8, 12];
        const snapped = valid.reduce((a, b) => Math.abs(b - v) < Math.abs(a - v) ? b : a);
        next.shots[idx] = { ...next.shots[idx], duration: snapped };
      } else {
        next.shots[idx] = { ...next.shots[idx], [key]: value };
      }
      return next;
    });
  };

  const onRemoveShot = (idx) => {
    setPlan((p) => {
      const next = { ...p, shots: p.shots.filter((_, i) => i !== idx) };
      return next;
    });
  };

  const onRender = async () => {
    setError("");
    setResult(null);
    if (!projectId || !plan?.shots?.length) return;
    if (plan.shots.length < 3 || plan.shots.length > 5) {
      setError("Commercial needs 3–5 shots before rendering.");
      return;
    }
    setRendering(true);
    try {
      const { data } = await api.post("/commercial/render", {
        project_id: projectId,
        aspect,
        quality,
        shots: plan.shots,
      });
      setResult(data);
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.failed_shots) {
        setError(`${detail.message} (shots: ${detail.failed_shots.map(f => f.index + 1).join(", ")})`);
      } else {
        setError(formatApiErrorDetail(detail) || e.message);
      }
    } finally {
      setRendering(false);
    }
  };

  const downloadHref = result ? `${BACKEND_URL}${result.download_url}` : null;

  return (
    <DashboardShell>
      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-10" data-testid="commercial-page">
        <div className="mb-7">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">
            <Sparkles className="w-3.5 h-3.5 gold-twinkle" /> Phase 11e · Multi-Shot Commercial
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold gold-shimmer">30/60-second commercials, auto-split</h1>
          <p className="text-[#A0A0A0] text-sm mt-2 max-w-2xl">
            Write one prompt. We split it into 3–5 cinematic shots, render each with Sora 2, and stitch them into one seamless commercial — all in a single click.
            {tier === "premium" && <span className="text-[#D4AF37] ml-2">· Premium: Sora 2 Pro per shot.</span>}
          </p>
        </div>

        {!plan && (
          <div className="space-y-5 border border-[#1F1F1F] bg-[#0A0A0A] p-5 sm:p-6" data-testid="commercial-brief-form">
            <Field label="Concept" required>
              <textarea
                rows={4}
                data-testid="commercial-concept"
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                placeholder="e.g. A 30-second commercial for a matte-black titanium dive watch. Hero on rooftop at golden hour, hand reveal, ocean B-roll, end card with 'Ready For Depth.'"
                className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Selector label="Total length" value={totalLength} options={[{id: 30, label: "30 seconds"}, {id: 60, label: "60 seconds"}]} onChange={(v) => setTotalLength(parseInt(v))} testId="commercial-length" />
              <Selector label="Platform" value={platform} options={PLATFORMS} onChange={(v) => { setPlatform(v); const p = PLATFORMS.find(x => x.id === v); if (p) setAspect(p.aspect); }} testId="commercial-platform" />
              <Selector label="Tone" value={tone} options={TONES} onChange={setTone} testId="commercial-tone" />
              <Selector label="Aspect" value={aspect} options={[{id: "9:16", label: "Vertical · 9:16"}, {id: "1:1", label: "Square · 1:1"}, {id: "16:9", label: "Widescreen · 16:9"}]} onChange={setAspect} testId="commercial-aspect" />
              <Selector label="Quality" value={quality} options={[{id: "pro", label: "Pro (Sora 2)"}, {id: "cinema", label: "Cinema (Sora 2 Pro)"}]} onChange={setQuality} testId="commercial-quality" />
              <Field label="Audience (optional)">
                <input data-testid="commercial-audience" value={audience} onChange={(e) => setAudience(e.target.value)}
                  placeholder="e.g. high-end watch collectors, US/UK, 30–55"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]" />
              </Field>
            </div>

            {error && <div className="px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="commercial-error">{error}</div>}

            <button
              onClick={onPlan}
              disabled={planning}
              data-testid="commercial-plan-btn"
              className="w-full inline-flex items-center justify-center gap-2 gold-sheen font-bold py-3.5 text-sm uppercase tracking-wider transition-all disabled:opacity-50 shadow-[0_8px_32px_-8px_rgba(212,175,55,0.55)]"
            >
              {planning ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting shot list…</> : <><Wand2 className="w-4 h-4" /> Plan my commercial</>}
            </button>
            <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider text-center">Plan stage debits 3 credits · Render stage debits per-shot Sora cost</p>
          </div>
        )}

        {plan && !result && (
          <div className="space-y-5" data-testid="commercial-plan">
            <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#0A0A0A] to-[#121212] p-5 sm:p-6">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-2">Director's brief</p>
              <h2 className="font-display text-2xl font-extrabold text-[#F4F4F0]">{plan.title}</h2>
              <p className="text-sm text-[#A0A0A0] mt-1">{plan.tagline}</p>
              <p className="text-xs text-[#5A5A5A] mt-2 uppercase tracking-wider">{plan.shots.length} shots · {totalSec}s total · target {totalLength}s</p>
            </div>

            {plan.shots.map((shot, idx) => (
              <div key={idx} data-testid={`commercial-shot-${idx}`} className="border border-[#2A2A2A] bg-[#0A0A0A] p-4 sm:p-5 gold-glow">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 bg-[#D4AF37] text-[#080808] font-bold text-xs">{idx + 1}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-semibold">{shot.role?.replace(/_/g, " ") || `shot ${idx+1}`}</span>
                  </div>
                  {plan.shots.length > 3 && (
                    <button onClick={() => onRemoveShot(idx)} data-testid={`commercial-shot-remove-${idx}`} className="text-[#A0A0A0] hover:text-red-300 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <textarea
                  rows={3}
                  data-testid={`commercial-shot-prompt-${idx}`}
                  value={shot.visual_prompt}
                  onChange={(e) => onEditShot(idx, "visual_prompt", e.target.value)}
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-sm text-[#F4F4F0] resize-none"
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Duration (s)</label>
                    <select value={shot.duration} onChange={(e) => onEditShot(idx, "duration", e.target.value)} data-testid={`commercial-shot-dur-${idx}`}
                      className="w-full mt-1 bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm text-[#F4F4F0]">
                      {[4, 8, 12].map(d => <option key={d} value={d}>{d}s</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">On-screen text (optional)</label>
                    <input value={shot.on_screen_text || ""} onChange={(e) => onEditShot(idx, "on_screen_text", e.target.value)}
                      className="w-full mt-1 bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm text-[#F4F4F0]" />
                  </div>
                </div>
              </div>
            ))}

            <CreditCostStrip feature="video" quality={quality} duration={totalSec} label={`${plan.shots.length} shots · ${totalSec}s · ${quality}`} />

            {error && <div className="px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="commercial-render-error">{error}</div>}

            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => { setPlan(null); setProjectId(null); }} data-testid="commercial-restart"
                className="inline-flex items-center justify-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] px-5 py-3 text-sm transition-colors">
                <RotateCcw className="w-3.5 h-3.5" /> Start over
              </button>
              <button onClick={onRender} disabled={rendering} data-testid="commercial-render-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 gold-sheen font-bold py-3.5 text-sm uppercase tracking-wider transition-all disabled:opacity-50">
                {rendering ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering {plan.shots.length} shots… (8–20 min)</> : <><Film className="w-4 h-4" /> Render all shots · Stitch into one MP4</>}
              </button>
            </div>
            <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider text-center">Atomic: all shots succeed or every credit is refunded</p>
          </div>
        )}

        {result && (
          <div className="space-y-5" data-testid="commercial-result">
            <video src={`${BACKEND_URL}${result.preview_url}`} controls playsInline className="w-full h-auto max-h-[70vh] bg-black" />
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
              <span className="text-xs text-[#A0A0A0]">{result.shot_count} shots · {result.total_seconds}s · stitched with crossfade</span>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => { setPlan(null); setProjectId(null); setResult(null); }} className="border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] px-4 py-2.5 text-sm transition-colors inline-flex items-center gap-2">
                  <RotateCcw className="w-3.5 h-3.5" /> New commercial
                </button>
                <button onClick={() => setExportOpen(true)} data-testid="commercial-smart-export"
                  className="border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[#D4AF37] px-4 py-2.5 text-sm transition-colors inline-flex items-center gap-2">
                  <Share2 className="w-3.5 h-3.5" /> Smart Export
                </button>
                <a href={downloadHref} data-testid="commercial-download" className="border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] px-4 py-2.5 text-sm transition-colors inline-flex items-center gap-2">
                  <Download className="w-3.5 h-3.5" /> Download MP4
                </a>
              </div>
            </div>
          </div>
        )}

        <SmartExportModal open={exportOpen} onClose={() => setExportOpen(false)} renderId={result?.render_id} sourceAspect={aspect} sourceKind="video" />
      </div>
    </DashboardShell>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </span>
      {children}
    </label>
  );
}

function Selector({ label, value, options, onChange, testId }) {
  return (
    <Field label={label}>
      <select data-testid={testId} value={value} onChange={(e) => onChange(typeof options[0].id === "number" ? Number(e.target.value) : e.target.value)}
        className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]">
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </Field>
  );
}
